import { createFileRoute, Link } from "@tanstack/react-router";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  FileSearch,
  FolderOpen,
  Layers,
  Repeat2,
  ShieldCheck,
  Trophy,
  Users,
  X,
} from "lucide-react";
import {
  claimsForItem,
  currency,
  formatRankValue,
  rankBasisMeta,
  rankedItems,
  top5ViewMeta,
  type RankBasis,
  type Top5Item,
  type Top5View,
  type Trend,
} from "@/lib/top5-data";
import { CreateProblemRecordDialog } from "@/components/CreateProblemRecordDialog";
import { recordForTop5Item, top5SourceLabel, useProblemRecords } from "@/lib/problem-record-store";
import { currentStageLabel } from "@/lib/problem-record-workflow";
import { LiveTop5 } from "@/components/LiveTop5";
import { usePortalRuntime } from "@/lib/portal-runtime";

export const Route = createFileRoute("/top5")({
  head: () => ({
    meta: [
      { title: "Top 5 Intelligence — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Ranked warranty prioritization across problems, components, products, car lines, suppliers and failure modes, with claim-level evidence and Problem Record linkage.",
      },
      { property: "og:title", content: "Top 5 Intelligence — Broadview Warranty Intelligence" },
      {
        property: "og:description",
        content:
          "Management prioritization intelligence: rank warranty issues by cost, rate, severity, trend, customer impact or recurrence.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PortalTop5Page,
});

const severityTone: Record<string, string> = {
  Critical: "border-destructive/40 bg-destructive/10 text-destructive",
  High: "border-warning/40 bg-warning/10 text-warning",
  Medium: "border-primary/40 bg-primary/10 text-primary",
  Low: "border-border bg-muted text-muted-foreground",
};

function TrendPill({ trend, pct }: { trend: Trend; pct: number }) {
  const Icon = trend === "Rising" ? ArrowUpRight : trend === "Falling" ? ArrowDownRight : Minus;
  const tone =
    trend === "Rising"
      ? "text-destructive"
      : trend === "Falling"
        ? "text-success"
        : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${tone}`}>
      <Icon className="h-3.5 w-3.5" />
      {pct > 0 ? "+" : ""}
      {pct.toFixed(1)}% · {trend}
    </span>
  );
}

function PortalTop5Page() {
  const { mode } = usePortalRuntime();
  return mode === "live" ? <LiveTop5 /> : <Top5PrototypePage />;
}

function Top5PrototypePage() {
  const [view, setView] = useState<Top5View>("problems");
  const [basis, setBasis] = useState<RankBasis>("claims");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState<Top5Item | null>(null);

  const records = useProblemRecords();

  const items = useMemo(() => {
    return rankedItems(view, basis).map((i) => {
      // Live linkage first; otherwise fall back to the seeded link on the item itself.
      const rec =
        recordForTop5Item(records, i) ??
        (i.problemRecordId ? records.find((r) => r.id === i.problemRecordId) : undefined);
      return rec
        ? {
            ...i,
            problemRecordId: rec.id,
            problemRecordStatus: `${rec.managementStatus ?? "Active"} · ${currentStageLabel(rec)}`,
          }
        : { ...i, problemRecordId: undefined, problemRecordStatus: undefined };
    });
  }, [view, basis, records]);

  const selected = selectedId ? (items.find((i) => i.id === selectedId) ?? null) : null;
  const activeView = top5ViewMeta.find((v) => v.key === view)!;
  const activeBasis = rankBasisMeta.find((b) => b.key === basis)!;

  const totals = useMemo(
    () => ({
      claims: items.reduce((s, i) => s + i.claims, 0),
      cost: items.reduce((s, i) => s + i.cost, 0),
      linked: items.filter((i) => i.problemRecordId).length,
    }),
    [items],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <Trophy className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Top 5 Intelligence</div>
              <div className="text-xs text-muted-foreground">
                Management prioritization · Broadview
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
            </Link>
            <Link
              to="/problem-records"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
            >
              <FolderOpen className="h-3.5 w-3.5" /> Problem Records
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">
        <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{activeView.label}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{activeView.blurb}</p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div className="rounded-lg border border-border bg-card px-4 py-2">
              <div className="text-muted-foreground">Top 5 claims</div>
              <div className="text-lg font-semibold">{totals.claims.toLocaleString()}</div>
            </div>
            <div className="rounded-lg border border-border bg-card px-4 py-2">
              <div className="text-muted-foreground">Top 5 exposure</div>
              <div className="text-lg font-semibold">{currency(totals.cost)}</div>
            </div>
            <div className="rounded-lg border border-border bg-card px-4 py-2">
              <div className="text-muted-foreground">Linked Problem Records</div>
              <div className="text-lg font-semibold">
                {totals.linked}
                <span className="text-sm text-muted-foreground"> / 5</span>
              </div>
            </div>
          </div>
        </section>

        {/* View selector */}
        <section className="flex flex-wrap gap-2">
          {top5ViewMeta.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`rounded-md border px-3.5 py-2 text-xs font-medium transition-colors ${
                v.key === view
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {v.label}
            </button>
          ))}
        </section>

        {/* Ranking basis */}
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Layers className="h-3.5 w-3.5" /> Rank by
            </span>
            {rankBasisMeta.map((b) => (
              <button
                key={b.key}
                onClick={() => setBasis(b.key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  b.key === basis
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {b.label}
              </button>
            ))}
            <span className="ml-auto rounded-md bg-primary/10 px-3 py-1 text-xs text-primary">
              Ranking basis: <strong>{activeBasis.label}</strong>
            </span>
          </div>
        </section>

        {/* Ranked list */}
        <section className="space-y-3">
          {items.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => setSelectedId(item.id)}
              className="group w-full rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-primary/50 hover:bg-muted/40"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="flex items-center gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-xl font-bold ${
                      idx === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{item.name}</span>
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${severityTone[item.severity]}`}
                      >
                        {item.severity} · Sev {item.severityScore}
                      </span>
                      {item.repeatOccurrence && (
                        <span className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning">
                          <Repeat2 className="h-3 w-3" /> Repeat ×{item.repeatCount}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="grid flex-1 grid-cols-2 gap-4 text-xs md:grid-cols-5 lg:ml-auto lg:max-w-3xl">
                  <div>
                    <div className="text-muted-foreground">Claims</div>
                    <div className="text-sm font-semibold">{item.claims.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Warranty cost</div>
                    <div className="text-sm font-semibold">{currency(item.cost)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">C / 1,000</div>
                    <div className="text-sm font-semibold">
                      {item.ratePer1000 != null ? item.ratePer1000.toFixed(1) : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Trend</div>
                    <TrendPill trend={item.trend} pct={item.trendPct} />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Customer impact</div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
                      <div
                        className="h-1.5 rounded-full bg-primary"
                        style={{ width: `${item.customerImpact}%` }}
                      />
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {item.customerImpact} / 100
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-[11px]">
                <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                  {item.carLine}
                </span>
                <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                  {item.component}
                </span>
                <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                  {item.supplier}
                </span>
                <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                  {item.failureMode}
                </span>
                {item.problemRecordId ? (
                  <span className="inline-flex items-center gap-1 rounded border border-success/40 bg-success/10 px-2 py-0.5 font-medium text-success">
                    <ShieldCheck className="h-3 w-3" /> {item.problemRecordId} ·{" "}
                    {item.problemRecordStatus}
                  </span>
                ) : (
                  <span className="rounded border border-border px-2 py-0.5 text-muted-foreground">
                    No Problem Record
                  </span>
                )}
                <span className="ml-auto inline-flex items-center gap-1 font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  {formatRankValue(item, basis)} · View evidence <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </button>
          ))}
        </section>
      </main>

      {selected && (
        <DrillDown
          item={selected}
          onClose={() => setSelectedId(null)}
          onCreate={() => setCreating(selected)}
        />
      )}
      {creating && (
        <CreateProblemRecordDialog
          item={creating}
          view={top5SourceLabel[creating.view]}
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  );
}

function DrillDown({
  item,
  onClose,
  onCreate,
}: {
  item: Top5Item;
  onClose: () => void;
  onCreate: () => void;
}) {
  const claims = claimsForItem(item);
  const claimCost = claims.reduce((s, c) => s + c.cost, 0);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-background/70 backdrop-blur-sm">
      <button className="flex-1" aria-label="Close drill-down" onClick={onClose} />
      <div className="flex h-full w-full max-w-4xl flex-col overflow-y-auto border-l border-border bg-card">
        <div className="sticky top-0 z-10 flex items-start gap-4 border-b border-border bg-card px-6 py-5">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Issue detail · underlying evidence
            </div>
            <h2 className="mt-1 text-lg font-semibold">{item.name}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded-md border border-border p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              { l: "Claims", v: item.claims.toLocaleString() },
              { l: "Warranty cost", v: currency(item.cost) },
              { l: "C / 1,000", v: item.ratePer1000 != null ? item.ratePer1000.toFixed(1) : "—" },
              { l: "Severity", v: `${item.severity} · ${item.severityScore}` },
            ].map((k) => (
              <div key={k.l} className="rounded-lg border border-border bg-background p-3">
                <div className="text-[11px] text-muted-foreground">{k.l}</div>
                <div className="mt-0.5 text-base font-semibold">{k.v}</div>
              </div>
            ))}
          </div>

          {item.spcSignal && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
              SPC signal — {item.spcSignal}
            </div>
          )}

          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <FileSearch className="h-4 w-4 text-primary" /> Claim evidence
              <span className="text-xs font-normal text-muted-foreground">
                {claims.length} sample rows · {currency(claimCost)} in this sample
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[900px] text-left text-xs">
                <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Claim</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Product / Car line</th>
                    <th className="px-3 py-2">Component · Supplier</th>
                    <th className="px-3 py-2">Failure mode</th>
                    <th className="px-3 py-2 text-right">Cost</th>
                    <th className="px-3 py-2 text-right">Mileage / MIS</th>
                    <th className="px-3 py-2">Sev</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c) => (
                    <tr key={c.id} className="border-t border-border align-top">
                      <td className="px-3 py-2 font-medium">{c.id}</td>
                      <td className="px-3 py-2 text-muted-foreground">{c.date}</td>
                      <td className="px-3 py-2">
                        {c.product}
                        <div className="text-[11px] text-muted-foreground">{c.carLine}</div>
                      </td>
                      <td className="px-3 py-2">
                        {c.component}
                        <div className="text-[11px] text-muted-foreground">{c.supplier}</div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{c.failureMode}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        ${c.cost.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {c.mileage.toLocaleString()} mi
                        <div className="text-[11px]">{c.mis} MIS</div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded border px-1.5 py-0.5 text-[10px] ${severityTone[c.severity]}`}
                        >
                          {c.severity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-semibold">Complaint / repair narratives</div>
            {claims.slice(0, 4).map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-background p-3 text-xs">
                <div className="font-medium">
                  {c.id} · {c.dealer}
                </div>
                <p className="mt-1 text-muted-foreground">
                  <span className="text-foreground">Complaint:</span> {c.complaint}
                </p>
                <p className="mt-0.5 text-muted-foreground">
                  <span className="text-foreground">Repair:</span> {c.repair}
                </p>
              </div>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-background p-3 text-xs">
              <div className="flex items-center gap-1.5 font-semibold">
                <Users className="h-3.5 w-3.5 text-primary" /> Attribution
              </div>
              <dl className="mt-2 space-y-1 text-muted-foreground">
                <div className="flex justify-between">
                  <dt>Car line</dt>
                  <dd className="text-foreground">{item.carLine}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Component</dt>
                  <dd className="text-foreground">{item.component}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Supplier</dt>
                  <dd className="text-foreground">{item.supplier}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Failure mode</dt>
                  <dd className="text-foreground">{item.failureMode}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Linked 8D</dt>
                  <dd className="text-foreground">{item.linkedCaseId ?? "—"}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border border-border bg-background p-3 text-xs">
              <div className="font-semibold">Prioritization signals</div>
              <dl className="mt-2 space-y-1 text-muted-foreground">
                <div className="flex justify-between">
                  <dt>Trend</dt>
                  <dd>
                    <TrendPill trend={item.trend} pct={item.trendPct} />
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Repeat occurrence</dt>
                  <dd className="text-foreground">
                    {item.repeatOccurrence ? `Yes — ${item.repeatCount} prior` : "No"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt>Customer impact</dt>
                  <dd className="text-foreground">{item.customerImpact} / 100</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Problem Record</dt>
                  <dd className="text-foreground">{item.problemRecordId ?? "None"}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
            {item.problemRecordId ? (
              <Link
                to="/problem-record/$id"
                params={{ id: item.problemRecordId }}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <FolderOpen className="h-4 w-4" /> Open Problem Record {item.problemRecordId}
              </Link>
            ) : (
              <button
                onClick={onCreate}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <FolderOpen className="h-4 w-4" /> Create Problem Record
              </button>
            )}
            <Link
              to="/problem-records"
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
            >
              Problem Record Registry
            </Link>
            <span className="text-xs text-muted-foreground">
              {item.problemRecordStatus
                ? `Current status: ${item.problemRecordStatus}`
                : "No closed-loop record exists for this issue yet."}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
